import React, { useState } from 'react';
import { useWalletKit } from '@mysten/wallet-kit';
import './EnhancedUserLogin.css';

interface EnhancedUserLoginProps {
  onLoginSuccess: (userData: any, sessionToken: string) => void;
  onSignupClick: () => void;
  onClose: () => void;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '/api';

const EnhancedUserLogin: React.FC<EnhancedUserLoginProps> = ({
  onLoginSuccess,
  onSignupClick,
  onClose
}) => {
  const { currentAccount, isConnected } = useWalletKit();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleWalletLogin = async () => {
    if (!isConnected || !currentAccount) {
      setError('Please connect your wallet first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/auth/user/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress: currentAccount.address,
          profileData: {}
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        localStorage.setItem('userSessionToken', result.sessionToken);
        localStorage.setItem('userData', JSON.stringify(result.user));
        
        setTimeout(() => {
          onLoginSuccess(result.user, result.sessionToken);
        }, 1500);
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enhanced-login-overlay">
      <div className="enhanced-login-container">
        {success && (
          <div className="success-banner">
            Login Successful
            <button className="close-success" onClick={() => setSuccess(false)}>×</button>
          </div>
        )}
        
        <div className="login-header">
          <div className="logo-container">
            <img src="/image.png" alt="Logo" className="login-logo" />
          </div>
          <h1>Sign in</h1>
          <p>with your Sui Wallet</p>
        </div>

        <div className="login-form">
          {isConnected && currentAccount ? (
            <div className="wallet-info">
              <div className="wallet-address-field">
                <input
                  type="text"
                  value={`${currentAccount.address.slice(0, 6)}...${currentAccount.address.slice(-4)}`}
                  readOnly
                  className="wallet-input"
                />
                <span className="wallet-icon">👛</span>
              </div>
              
              <button
                className="sign-in-button"
                onClick={handleWalletLogin}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          ) : (
            <div className="connect-wallet-section">
              <p className="connect-message">Please connect your wallet to continue</p>
              <button className="connect-wallet-button">
                Connect Wallet
              </button>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="divider">
            <span>OR</span>
          </div>

          <div className="signup-link">
            <span>Don't have an account? </span>
            <button onClick={onSignupClick} className="link-button">
              Sign up here
            </button>
          </div>

          <button className="close-button" onClick={onClose}>
            Back to App
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedUserLogin;
